import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { agentReducer, initialAgentState, resolvedApprovalNotifications } from '../components/agent/agentReducer.js';
import { openCasePlan, createCaseChooser } from '../components/agent/agentUi.js';
import {
  validateServerMessage, isValidClientId, buildHello, pingMessage, focusMessage, caseOpenMessage,
  intentMessage, controlMessage, decideMessage, draftOpMessage, commentMessage, roleMessage, replayMessage,
  resolveAgentUrl,
} from '../components/agent/agentProtocol.js';
import { focusChanged } from '../components/agent/agentFocus.js';
import { agentWsUrl, agentBaseUrl } from '../components/agent/agentConfig.js';

/**
 * useAgent — browser client for the mhi-agent runtime (CONTRACT §2.3, §10.3).
 *
 *   const agent = useAgent({ url, enabled, clientId, demoRole, onUiAction, onCaseFocus });
 *
 * One WebSocket for live events; REST (AGENT_BASE_URL + /v1/...) for memory, policy, audit,
 * events, share and scenarios. Every inbound frame goes through validateServerMessage, then the
 * pure agentReducer. Connection rules follow useVoiceSession: refs for the socket and the active
 * flag (StrictMode connect, close, connect), reconnect backoff min(8000, 1000·2^n) for at most 8
 * attempts, then status 'closed' with a manual reconnect(); ping every 20 s; `hello` with
 * last_seq = the last seq seen on every open, so a reconnect replays only what was missed.
 *
 * ui.action: onUiAction(action, {asset, caption, case_id, seq, tool_call_id}) for live actions only.
 * Replayed actions are held; on replay.done only the last one of the active case is applied, so the
 * stage matches the case. After a snapshot (a fresh page load) the last ui.action of the active
 * case is fetched from its stored events and applied the same way, so the stage survives a reload.
 *
 * The hook connects whenever `enabled`, also outside agent mode, so proactive notifications reach
 * the normal view. activeCaseId starts from ?case= and is mirrored into the URL with replaceState.
 * With `autoOpen` (agent mode), the first snapshot of a page load opens the most recently updated open
 * case by itself (autoOpenCaseId) unless a case was already chosen: a ?case= deep link, the user
 * (setActiveCase, including clearing it) or a voice / agent case.focus. Later snapshots never do.
 * currentCaseId() returns the live active case (updated before onUiAction runs on a case switch);
 * route ui.actions against it, never against a copy synced after the next render.
 *
 * A decided approval resolves its case's approval_needed notifications: the reducer marks them read
 * and the hook marks them read on the server too, so a reload does not show them again.
 *
 * Status: idle | connecting | open | reconnecting | closed | error
 */

const PING_MS = 20000;
const MAX_RECONNECTS = 8;
const FOCUS_MIN_MS = 250;            // focus frames at most 4 per second
const EVENTS_PAGE = 2000;
const EVENTS_MAX = 20000;
// Client close code asking for an immediate reconnect from seq 0 (the server's seq went backwards).
const RESYNC_CODE = 4000;

function readCaseParam() {
  try {
    const id = new URLSearchParams(window.location.search).get('case');
    return id && /^[A-Za-z0-9_-]{1,64}$/.test(id) ? id : null;
  } catch {
    return null;
  }
}

function writeCaseParam(id) {
  try {
    const u = new URL(window.location.href);
    if ((u.searchParams.get('case') || null) === (id || null)) return;
    if (id) u.searchParams.set('case', id); else u.searchParams.delete('case');
    window.history.replaceState(window.history.state, '', `${u.pathname}${u.search}${u.hash}`);
  } catch {
    /* history unavailable: the URL simply does not follow */
  }
}

const encodePath = (path) => String(path || '').split('/').map(encodeURIComponent).join('/');

export default function useAgent({
  url,
  enabled = true,
  clientId,
  demoRole = null,
  onUiAction,
  onCaseFocus,
  /** REST base; defaults to AGENT_BASE_URL resolved against the page. */
  baseUrl,
  /** Agent mode: the first snapshot opens the most recent open case when none was chosen. */
  autoOpen = false,
} = {}) {
  // Explicit values may be relative (the dev proxy path): resolve them against the page too.
  const pageLoc = typeof window !== 'undefined' ? window.location : null;
  const wsUrl = url !== undefined && url !== null ? resolveAgentUrl(url, pageLoc, { ws: true }) : agentWsUrl();
  const restBase = baseUrl !== undefined && baseUrl !== null ? resolveAgentUrl(baseUrl, pageLoc) : agentBaseUrl();

  const [state, dispatch] = useReducer(agentReducer, initialAgentState);
  const [status, setStatus] = useState('idle');
  const [activeCaseId, setActiveCaseIdState] = useState(readCaseParam);
  const [retryNonce, setRetryNonce] = useState(0);

  const wsRef = useRef(null);
  const seqRef = useRef(0);
  const stateRef = useRef(state);
  const casesRef = useRef({});
  const activeCaseRef = useRef(activeCaseId);
  const replayUiRef = useRef(new Map());
  const restoreGenRef = useRef(0);
  const focusRef = useRef({ latest: null, sent: null, sentAt: 0, timer: null });
  const restBaseRef = useRef(restBase);
  const demoRoleRef = useRef(demoRole);
  const clientIdRef = useRef(clientId);
  const onUiActionRef = useRef(onUiAction);
  const onCaseFocusRef = useRef(onCaseFocus);
  const autoOpenRef = useRef(autoOpen);
  // Auto-open on the first snapshot, never over a deep link, a user choice or a voice focus.
  const chooserRef = useRef(null);
  if (chooserRef.current === null) chooserRef.current = createCaseChooser({ initialCaseId: activeCaseId });

  useEffect(() => {
    stateRef.current = state;
    restBaseRef.current = restBase;
    demoRoleRef.current = demoRole;
    clientIdRef.current = clientId;
    onUiActionRef.current = onUiAction;
    onCaseFocusRef.current = onCaseFocus;
    autoOpenRef.current = autoOpen;
  });

  /* ---- sending -------------------------------------------------------- */
  const send = useCallback((msg) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(JSON.stringify(msg));
      return true;
    } catch {
      return false;
    }
  }, []);

  /* ---- REST ----------------------------------------------------------- */
  const rest = useCallback(async (path, { method = 'GET', body } = {}) => {
    const base = restBaseRef.current;
    if (!base) throw new Error('The agent API is not configured');
    const headers = { Accept: 'application/json' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    const principal = stateRef.current.principal;
    const role = principal?.source === 'demo' ? principal.roles?.[0] : demoRoleRef.current;
    if (role) headers['X-MHI-Demo-Role'] = role;
    const res = await fetch(`${base}${path}`, {
      method, headers, credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    let data = null;
    try { data = await res.json(); } catch { data = null; }
    if (!res.ok) {
      const err = new Error(data?.error?.message || `Request failed (${res.status})`);
      err.code = data?.error?.code || 'http';
      err.status = res.status;
      throw err;
    }
    return data;
  }, []);

  const events = useCallback(async (caseId) => {
    const out = [];
    let after = 0;
    while (out.length < EVENTS_MAX) {
      const page = await rest(`/v1/cases/${encodeURIComponent(caseId)}/events?after_seq=${after}&limit=${EVENTS_PAGE}`);
      const list = Array.isArray(page?.events) ? page.events : [];
      out.push(...list);
      if (list.length < EVENTS_PAGE) break;
      after = list[list.length - 1].seq ?? after;
    }
    return out;
  }, [rest]);

  /* ---- inbound -------------------------------------------------------- */
  const callUi = useCallback((msg) => {
    const fn = onUiActionRef.current;
    if (typeof fn !== 'function') return;
    const d = msg.data || {};
    fn(d.action, {
      asset: d.asset ?? null, caption: d.caption ?? null, case_id: msg.case_id ?? null,
      seq: msg.seq ?? null, tool_call_id: d.tool_call_id ?? null,
    });
  }, []);

  const switchActive = useCallback((caseId) => {
    activeCaseRef.current = caseId || null;
    setActiveCaseIdState(caseId || null);
  }, []);

  /** After a snapshot: put the stage where the active case left it (its last stored ui.action). */
  const restoreLastUi = useCallback((caseId) => {
    if (!caseId || !restBaseRef.current) return;
    restoreGenRef.current += 1;
    const gen = restoreGenRef.current;
    events(caseId).then((list) => {
      if (gen !== restoreGenRef.current || activeCaseRef.current !== caseId) return;
      const last = [...list].reverse().find((e) => e.type === 'ui.action' && validateServerMessage(e).ok);
      if (!last) return;
      const d = last.data;
      dispatch({
        type: 'local.last_ui',
        lastUi: { action: d.action, asset: d.asset ?? null, caption: d.caption ?? null, case_id: caseId, seq: last.seq, tool_call_id: d.tool_call_id ?? null },
      });
      callUi(last);
    }).catch((err) => console.warn('[Agent] could not restore the stage:', err.message));
  }, [events, callUi]);

  const handleMessage = useCallback((raw) => {
    const v = validateServerMessage(raw);
    if (!v.ok) {
      console.warn(`[Agent] dropped frame (${v.reason})`, raw?.type);
      return;
    }
    const msg = v.msg;
    if (msg.type === 'welcome' && Number.isInteger(msg.server_seq) && msg.server_seq < seqRef.current) {
      // A fresh server database: drop what we hold (the reducer resets too) and reconnect from 0 for
      // a snapshot, instead of ignoring every new event as already seen.
      seqRef.current = 0;
      replayUiRef.current.clear();
      dispatch(msg);
      const ws = wsRef.current;
      if (ws) { try { ws.close(RESYNC_CODE, 'resync'); } catch { /* already closing */ } }
      return;
    }
    if (msg.type === 'snapshot') seqRef.current = msg.seq;
    else if (Number.isInteger(msg.seq) && msg.seq > seqRef.current) seqRef.current = msg.seq;

    if ((msg.type === 'case.created' || msg.type === 'case.updated') && msg.data.case?.id) {
      casesRef.current = { ...casesRef.current, [msg.data.case.id]: msg.data.case };
    }
    if (msg.type === 'snapshot') {
      const next = {};
      for (const c of msg.data.cases || []) if (c?.id) next[c.id] = c;
      casesRef.current = next;
    }

    // Notifications an approval decision resolves (computed against the state as it will be).
    let resolved = [];
    if (msg.type === 'approval.updated' && !msg.replayed && msg.data.approval?.id) {
      const a = msg.data.approval;
      const held = stateRef.current;
      resolved = resolvedApprovalNotifications(
        { ...held, approvals: { ...held.approvals, [a.id]: a } }, a.case_id ?? msg.case_id ?? null,
      );
    }

    dispatch(msg);

    for (const id of resolved) {
      if (!restBaseRef.current) break;
      rest(`/v1/notifications/${encodeURIComponent(id)}/read`, { method: 'POST', body: {} })
        .catch((err) => console.warn('[Agent] mark read failed:', err.message));
    }

    switch (msg.type) {
      case 'ui.action':
        if (msg.replayed) replayUiRef.current.set(msg.case_id ?? '', msg);
        else callUi(msg);
        break;
      case 'replay.done': {
        const held = replayUiRef.current.get(activeCaseRef.current ?? '');
        replayUiRef.current.clear();
        if (held) callUi(held);
        break;
      }
      case 'snapshot': {
        replayUiRef.current.clear();
        const pick = chooserRef.current.onSnapshot(msg.data.cases, {
          autoOpen: autoOpenRef.current, activeCaseId: activeCaseRef.current,
        });
        if (pick) {
          switchActive(pick);
          const fn = onCaseFocusRef.current;
          if (typeof fn === 'function') fn(casesRef.current[pick] ?? { id: pick });
        }
        restoreLastUi(activeCaseRef.current);
        break;
      }
      case 'case.focus': {
        if (msg.replayed) break;
        const caseId = msg.data.case_id ?? msg.case_id;
        if (!caseId) break;
        const reason = msg.data.reason;
        const none = !activeCaseRef.current;
        if (reason === 'voice' || (none && (reason === 'agent_opened' || reason === 'handoff'))) {
          chooserRef.current.choose();
          switchActive(caseId);
          const fn = onCaseFocusRef.current;
          if (typeof fn === 'function') {
            fn(casesRef.current[caseId] ?? { id: caseId, asset: msg.data.asset ?? null });
          }
        }
        break;
      }
      default:
        break;
    }
  }, [callUi, restoreLastUi, switchActive, rest]);

  /* ---- socket --------------------------------------------------------- */
  useEffect(() => {
    if (!enabled || !wsUrl || !isValidClientId(clientId)) {
      setStatus(enabled && wsUrl && !isValidClientId(clientId) ? 'error' : 'idle');
      return undefined;
    }
    let active = true;
    let socket = null;
    let attempts = 0;
    let reconnectTimer = null;
    let pingTimer = null;

    const connect = () => {
      if (!active) return;
      setStatus(attempts > 0 ? 'reconnecting' : 'connecting');
      let ws;
      try {
        ws = new WebSocket(wsUrl);
      } catch (e) {
        console.error('[Agent] socket failed', e);
        setStatus('error');
        return;
      }
      socket = ws;
      wsRef.current = ws;

      ws.onopen = () => {
        if (!active || socket !== ws) return;
        attempts = 0;
        setStatus('open');
        ws.send(JSON.stringify(buildHello({ clientId, lastSeq: seqRef.current, demoRole: demoRoleRef.current })));
        if (activeCaseRef.current) ws.send(JSON.stringify(caseOpenMessage(activeCaseRef.current)));
        const f = focusRef.current;
        if (f.latest) {
          ws.send(JSON.stringify(focusMessage(f.latest)));
          f.sent = f.latest;
          f.sentAt = Date.now();
        }
        clearInterval(pingTimer);
        pingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(pingMessage()));
        }, PING_MS);
      };
      ws.onmessage = (event) => {
        if (socket !== ws || typeof event.data !== 'string') return;
        let msg;
        try { msg = JSON.parse(event.data); } catch { return; }
        handleMessage(msg);
      };
      ws.onerror = () => console.warn('[Agent] socket error');
      ws.onclose = (event) => {
        clearInterval(pingTimer);
        if (wsRef.current === ws) wsRef.current = null;
        if (socket === ws) socket = null;
        if (!active) return;
        if (event.code === RESYNC_CODE) {
          setStatus('reconnecting');
          reconnectTimer = setTimeout(connect, 0);
          return;
        }
        // Origin rejected / unauthenticated: retrying cannot help.
        if (event.code === 1008 || event.code === 4401) {
          console.warn(`[Agent] closed by the server (${event.code})`);
          setStatus('error');
          return;
        }
        if (attempts >= MAX_RECONNECTS) {
          setStatus('closed');
          return;
        }
        const delay = Math.min(8000, 1000 * 2 ** attempts);
        attempts += 1;
        setStatus('reconnecting');
        reconnectTimer = setTimeout(connect, delay);
      };
    };

    connect();
    return () => {
      active = false;
      clearTimeout(reconnectTimer);
      clearInterval(pingTimer);
      if (socket) {
        socket.onclose = null;
        try { socket.close(1000); } catch { /* already closed */ }
      }
      if (wsRef.current === socket) wsRef.current = null;
      socket = null;
    };
  }, [enabled, wsUrl, clientId, retryNonce, handleMessage]);

  const reconnect = useCallback(() => setRetryNonce((n) => n + 1), []);

  /* ---- active case ---------------------------------------------------- */
  useEffect(() => {
    dispatch({ type: 'local.active_case', caseId: activeCaseId });
    writeCaseParam(activeCaseId);
    if (activeCaseId) send(caseOpenMessage(activeCaseId));
  }, [activeCaseId, send]);

  const setActiveCase = useCallback((id) => {
    chooserRef.current.choose();
    const plan = openCasePlan(id, { activeCaseId: activeCaseRef.current, lastUiByCase: stateRef.current.lastUiByCase });
    if (!plan.switch) return;
    // The ref changes first, so the onUiAction below already routes against the new case.
    switchActive(plan.caseId);
    if (plan.apply && typeof onUiActionRef.current === 'function') {
      // Opening another case puts the stage where that case left it.
      onUiActionRef.current(plan.apply.action, plan.apply.meta);
    } else if (plan.restore) {
      // Not seen live since the snapshot: its stored events know where the stage was.
      restoreLastUi(plan.caseId);
    }
  }, [switchActive, restoreLastUi]);

  /** The live active case (see the header); stable, safe to call from any callback. */
  const currentCaseId = useCallback(() => activeCaseRef.current, []);

  /* ---- focus (throttled, only on change) ------------------------------ */
  useEffect(() => {
    const f = focusRef.current;
    return () => clearTimeout(f.timer);
  }, []);

  const setFocus = useCallback((focus) => {
    const f = focusRef.current;
    f.latest = focus;
    const flush = () => {
      f.timer = null;
      if (!f.latest || !focusChanged(f.sent, f.latest)) return;
      if (send(focusMessage(f.latest))) {
        f.sent = f.latest;
        f.sentAt = Date.now();
      }
    };
    if (!focusChanged(f.sent, focus)) return;
    const wait = FOCUS_MIN_MS - (Date.now() - f.sentAt);
    if (wait <= 0) flush();
    else if (!f.timer) f.timer = setTimeout(flush, wait);
  }, [send]);

  /* ---- commands ------------------------------------------------------- */
  const sendIntent = useCallback((text) => {
    const t = String(text ?? '').trim();
    return t ? send(intentMessage(activeCaseRef.current, t)) : false;
  }, [send]);

  const control = useCallback((op, extra = {}) => {
    const caseId = extra?.case_id ?? activeCaseRef.current;
    return caseId ? send(controlMessage(caseId, op, extra)) : false;
  }, [send]);

  const decide = useCallback((approvalId, decision, opts = {}) => (
    send(decideMessage(approvalId, decision, opts))
  ), [send]);

  const draftOp = useCallback((draftId, baseVersion, op) => {
    const caseId = stateRef.current.drafts[draftId]?.case_id ?? activeCaseRef.current;
    return caseId ? send(draftOpMessage(caseId, draftId, baseVersion, op)) : false;
  }, [send]);

  const comment = useCallback((body, parentId = null) => {
    const caseId = activeCaseRef.current;
    const text = String(body ?? '').trim();
    return caseId && text ? send(commentMessage(caseId, text, parentId)) : false;
  }, [send]);

  const startReplay = useCallback((scenario, speed = 1, model = null) => {
    if (send(replayMessage(scenario, speed, model))) return Promise.resolve({ ok: true });
    const body = { scenario, speed };
    if (model) body.model = model;
    return rest('/v1/replay/start', { method: 'POST', body });
  }, [send, rest]);

  const setRole = useCallback((role) => {
    demoRoleRef.current = role;
    return send(roleMessage(role));
  }, [send]);

  const setWb = useCallback((patch) => dispatch({ type: 'local.wb', patch }), []);

  const markRead = useCallback((notificationId) => {
    dispatch({ type: 'local.notification_read', id: notificationId });
    if (!restBaseRef.current) return Promise.resolve(null);
    return rest(`/v1/notifications/${encodeURIComponent(notificationId)}/read`, { method: 'POST', body: {} })
      .catch((err) => console.warn('[Agent] mark read failed:', err.message));
  }, [rest]);

  const memory = useMemo(() => ({
    add(text, opts = {}) {
      const c = casesRef.current[activeCaseRef.current] || null;
      const body = {
        text: String(text ?? '').trim(),
        asset: opts.asset ?? c?.asset,
        kind: opts.kind ?? 'note',
      };
      const site = opts.site ?? c?.site?.id;
      const unit = opts.unit ?? c?.unit;
      if (site) body.site = site;
      if (unit) body.unit = unit;
      if (opts.scope) body.scope = opts.scope;
      return rest('/v1/memory', { method: 'POST', body });
    },
    edit(id, text) {
      return rest(`/v1/memory/${encodeURIComponent(id)}`, { method: 'PATCH', body: { text: String(text ?? '') } });
    },
    remove(id) {
      return rest(`/v1/memory/${encodeURIComponent(id)}`, { method: 'DELETE' });
    },
  }), [rest]);

  const policy = useMemo(() => ({
    list: () => rest('/v1/policy/rules'),
    patch: (id, patch) => rest(`/v1/policy/rules/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch }),
  }), [rest]);

  const audit = useCallback((caseId) => rest(`/v1/cases/${encodeURIComponent(caseId)}/audit`), [rest]);

  const share = useCallback(async (caseId) => {
    const r = await rest(`/v1/cases/${encodeURIComponent(caseId)}/share`, { method: 'POST', body: {} });
    return { ...r, url: r?.path ? `${restBaseRef.current}${r.path}` : null };
  }, [rest]);

  const scenarios = useCallback(() => rest('/v1/scenarios'), [rest]);

  const fileUrl = useCallback((caseId, path) => (
    caseId && path ? `${restBase}/v1/cases/${encodeURIComponent(caseId)}/files/${encodePath(path)}` : ''
  ), [restBase]);

  return useMemo(() => ({
    status,
    state,
    error: state.lastError,
    clientId,
    activeCaseId,
    currentCaseId,
    setActiveCase,
    sendIntent,
    control,
    decide,
    draftOp,
    comment,
    startReplay,
    setRole,
    setFocus,
    setWb,
    markRead,
    memory,
    policy,
    audit,
    events,
    share,
    scenarios,
    fileUrl,
    reconnect,
  }), [status, state, clientId, activeCaseId, currentCaseId, setActiveCase, sendIntent, control, decide, draftOp, comment,
    startReplay, setRole, setFocus, setWb, markRead, memory, policy, audit, events, share, scenarios, fileUrl,
    reconnect]);
}
